import type { ShortcutPlatform } from '@/lib/keyboard/types/shortcut-platform'
import type { ShortcutToken } from './types/shortcut-token'
import type { ShortcutCombo } from './types/shortcut-combo'
import type { ShortcutKey } from './types/shortcut-key'
import { shortcuts } from './constants/shortcut-registry'

export function getShortcutPlatform(): ShortcutPlatform {
  if (typeof navigator === 'undefined') return 'windows'

  const platform = navigator.platform.toLowerCase()
  const userAgent = navigator.userAgent.toLowerCase()
  const userAgentDataPlatform = (
    navigator as Navigator & { userAgentData?: { platform?: string } }
  ).userAgentData?.platform?.toLowerCase()

  if (
    platform.includes('mac') ||
    userAgent.includes('mac os') ||
    userAgentDataPlatform?.includes('mac')
  ) {
    return 'mac'
  }

  return 'windows'
}

export function getShortcutCombos(
  shortcutKey: ShortcutKey,
  platform = getShortcutPlatform()
): readonly ShortcutCombo[] {
  return shortcuts[shortcutKey][platform]
}

export function getShortcutTokenLabel(token: ShortcutToken, platform: ShortcutPlatform): string {
  if (token === 'mod') return platform === 'mac' ? '⌘' : 'Ctrl'
  if (token === 'shift') return platform === 'mac' ? '⇧' : 'Shift'
  if (token === 'alt') return platform === 'mac' ? '⌥' : 'Alt'
  if (token === 'backslash') return '\\'
  if (token === 'space') return 'Spacebar'
  if (token === 'delete') return 'Del'
  if (token === 'backspace') return 'Backspace'
  if (token === 'plus') return '+'
  if (token === 'minus') return '-'
  if (token === 'equals') return '='
  if (token === 'leftBracket') return '['
  if (token === 'rightBracket') return ']'
  if (token === 'slash') return '/'

  return token
}

/**
 * Builds a value for `aria-keyshortcuts`: space-separated shortcuts, each shortcut using `+`
 * between parts (WAI-ARIA). Uses spoken-friendly modifier names for assistive technology.
 */
export function formatAriaKeyshortcuts(
  shortcutKey: ShortcutKey,
  platform = getShortcutPlatform()
): string {
  const combos = getShortcutCombos(shortcutKey, platform)

  const tokenToAriaPart = (token: ShortcutToken): string => {
    if (token === 'mod') return platform === 'mac' ? 'Command' : 'Control'
    if (token === 'shift') return 'Shift'
    if (token === 'alt') return platform === 'mac' ? 'Option' : 'Alt'
    if (token === 'plus') return 'Plus'
    if (token === 'minus') return 'Minus'
    if (token === 'equals') return 'Equal'
    if (token === 'space') return 'Space'
    if (token === 'delete') return 'Delete'
    if (token === 'backspace') return 'Backspace'
    if (token === 'backslash') return 'Backslash'
    if (token === 'leftBracket') return 'BracketLeft'
    if (token === 'rightBracket') return 'BracketRight'
    if (token === 'slash') return 'Slash'

    return String(token)
  }

  return combos.map((combo) => combo.map((token) => tokenToAriaPart(token)).join('+')).join(' ')
}

/** Space-joins `formatAriaKeyshortcuts` for several actions (e.g. a zoom control exposing menu shortcuts). */
export function formatAriaKeyshortcutsMany(
  keys: ShortcutKey[],
  platform = getShortcutPlatform()
): string {
  return keys.map((key) => formatAriaKeyshortcuts(key, platform)).join(' ')
}
